/* Anya Johnson theme — sitewide interaction layer.
   Progressive enhancement only: every effect degrades to plain, readable content
   if JS fails or prefers-reduced-motion is set. No dependencies. */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- Scroll reveal ---------- */
  var REVEAL_SELECTOR = [
    ".aj-path-tile", ".aj-stat", ".aj-story-stat", ".aj-ep", ".aj-quote",
    ".aj-story-headline", ".aj-story-body-inner", ".aj-story-pull",
    ".aj-paths-head", ".aj-eps-head", ".aj-vault-card", ".aj-gallery-item",
    ".aj-contact-form", ".aj-subscribe-tile", "[data-aj-reveal]"
  ].join(",");

  function initReveal() {
    /* Elements already animated by assets/motion.js (marked data-aj-motion-claimed)
       are excluded here so the two systems never fight over the same element —
       motion.js's page-wiring runs earlier in deferred-script order, so its
       claims are already in the DOM by the time this runs. Anything it hasn't
       touched (pages not yet redesigned) keeps working exactly as before. */
    var els = Array.prototype.filter.call(
      document.querySelectorAll(REVEAL_SELECTOR),
      function (el) { return !el.hasAttribute("data-aj-motion-claimed"); }
    );
    if (!els.length) return;

    if (reduceMotion || !("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("aj-is-visible"); });
      return;
    }

    els.forEach(function (el, i) {
      el.classList.add("aj-reveal");
      el.style.setProperty("--aj-reveal-delay", (i % 6) * 70 + "ms");
    });

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("aj-is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Animated stat counters ---------- */
  function animateCount(el) {
    var raw = el.textContent.trim();
    var match = raw.match(/^([^\d]*)(\d[\d,]*)(.*)$/);
    if (!match) return;

    var prefix = match[1], numStr = match[2].replace(/,/g, ""), suffix = match[3];
    var target = parseInt(numStr, 10);
    if (isNaN(target) || target <= 0 || target > 100000) return;

    var duration = 1100;
    var start = null;

    function step(ts) {
      if (start === null) start = ts;
      var progress = Math.min((ts - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.round(eased * target);
      el.textContent = prefix + current.toLocaleString() + suffix;
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        el.textContent = raw;
      }
    }
    window.requestAnimationFrame(step);
  }

  function initCounters() {
    if (reduceMotion || !("IntersectionObserver" in window)) return;
    var els = document.querySelectorAll(".aj-stat-value, .aj-story-stat-value");
    if (!els.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animateCount(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.6 }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Magnetic buttons ---------- */
  function initMagneticButtons() {
    if (reduceMotion || matchMedia("(hover: none)").matches) return;
    var buttons = document.querySelectorAll(".aj-btn");

    buttons.forEach(function (btn) {
      var strength = 14;
      btn.addEventListener("mousemove", function (e) {
        var rect = btn.getBoundingClientRect();
        var x = e.clientX - rect.left - rect.width / 2;
        var y = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = "translate(" + (x / rect.width) * strength + "px," + (y / rect.height) * strength + "px)";
      });
      btn.addEventListener("mouseleave", function () {
        btn.style.transform = "";
      });
    });
  }

  /* ---------- Scroll progress bar ---------- */
  function initProgressBar() {
    var bar = document.createElement("div");
    bar.className = "aj-progress-bar";
    document.body.appendChild(bar);

    function onScroll() {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - doc.clientHeight;
      var pct = scrollable > 0 ? (doc.scrollTop / scrollable) * 100 : 0;
      bar.style.width = pct + "%";
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
  }

  /* ---------- Sticky header state ---------- */
  function initHeaderScroll() {
    var header = document.querySelector(".aj-header");
    if (!header) return;
    var last = window.scrollY;

    function onScroll() {
      var y = window.scrollY;
      header.classList.toggle("is-scrolled", y > 24);
      last = y;
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------- Lazy-loaded videos ----------
     Videos use data-aj-lazy-src instead of src/autoplay so the browser
     never fetches them until they're about to scroll into view — large
     autoplay videos loading immediately on page load was tanking initial
     load time site-wide. Respects prefers-reduced-motion by never loading
     them at all for those users (poster/placeholder stays static). */
  function initLazyVideos() {
    var videos = document.querySelectorAll("[data-aj-lazy-src]");
    if (!videos.length) return;

    if (reduceMotion) return; // never fetch/play for reduced-motion users

    if (!("IntersectionObserver" in window)) {
      videos.forEach(loadAndPlay);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            loadAndPlay(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "200px 0px" }
    );
    videos.forEach(function (v) { io.observe(v); });
  }

  function loadAndPlay(video) {
    var src = video.getAttribute("data-aj-lazy-src");
    if (!src || video.src) return;
    video.src = src;
    video.load();
    video.play().catch(function () {}); // ignore autoplay-blocked rejections
  }

  /* ---------- Init ---------- */
  function init() {
    initReveal();
    initCounters();
    initMagneticButtons();
    initHeaderScroll();
    initProgressBar();
    initLazyVideos();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
